import { ThemeOptions } from "@mui/material/styles";
import { dsSurface } from "@Themes/palettes/app-palette";
import type { CSSProperties } from "react";

type FontWeight = 400 | 500 | 600 | 700 | 800;
type SharedTypographyVariantPrefix =
  | "text9xl"
  | "text8xl"
  | "text7xl"
  | "text6xl"
  | "text5xl"
  | "text4xl"
  | "text3xl"
  | "text2xl"
  | "text1xl"
  | "textLg"
  | "textBase"
  | "textS"
  | "textXs";

type WeightedTypographyVariant = `${SharedTypographyVariantPrefix}${FontWeight}`;
type DecorativeTypographyVariant =
  | "textLgItalic"
  | "textLgUnderline"
  | "textBaseItalic"
  | "textBaseUnderline"
  | "textSItalic"
  | "textSUnderline"
  | "textXsItalic"
  | "textXsUnderline";
type SpecialTypographyVariant = "dialogTitle" | "dialogBody" | "h7";

export type ExtendedTypographyVariant =
  | WeightedTypographyVariant
  | DecorativeTypographyVariant
  | SpecialTypographyVariant;

const appFontFamily = [
  '"Inter"',
  '"-apple-system"',
  '"BlinkMacSystemFont"',
  "Telegraf",
  "Optician-Sans",
  '"Moki-Base"',
  '"Roboto"',
  '"Segoe UI"',
  '"Oxygen"',
  '"Ubuntu"',
  '"Cantarell"',
  '"Fira Sans"',
  '"Droid Sans"',
  '"Helvetica Neue"',
  '"sans-serif"',
].join(",");

const createWeightVariants = <TPrefix extends SharedTypographyVariantPrefix>(
  prefix: TPrefix,
  fontSize: string,
  lineHeight: string
): Record<`${TPrefix}${FontWeight}`, CSSProperties> =>
  ({
    [`${prefix}400`]: {
      fontWeight: 400,
      fontFamily: "Inter",
      fontSize,
      lineHeight,
    },
    [`${prefix}500`]: {
      fontWeight: 500,
      fontFamily: "Inter",
      fontSize,
      lineHeight,
    },
    [`${prefix}600`]: {
      fontWeight: 600,
      fontFamily: "Inter",
      fontSize,
      lineHeight,
    },
    [`${prefix}700`]: {
      fontWeight: 700,
      fontFamily: "Inter",
      fontSize,
      lineHeight,
    },
    [`${prefix}800`]: {
      fontWeight: 800,
      fontFamily: "Inter",
      fontSize,
      lineHeight,
    },
  }) as Record<`${TPrefix}${FontWeight}`, CSSProperties>;

const createDecorativeVariants = <TPrefix extends "textLg" | "textBase" | "textS" | "textXs">(
  prefix: TPrefix,
  fontSize: string,
  lineHeight: string
): Record<`${TPrefix}Italic` | `${TPrefix}Underline`, CSSProperties> =>
  ({
    [`${prefix}Italic`]: {
      fontStyle: "italic",
      fontWeight: 400,
      fontFamily: "Inter",
      fontSize,
      lineHeight,
    },
    [`${prefix}Underline`]: {
      textDecoration: "underline",
      fontWeight: 400,
      fontFamily: "Inter",
      fontSize,
      lineHeight,
    },
  }) as Record<`${TPrefix}Italic` | `${TPrefix}Underline`, CSSProperties>;

export const customTypographyVariants: Record<ExtendedTypographyVariant, CSSProperties> = {
  ...createWeightVariants("text9xl", "64px", "72px"),
  ...createWeightVariants("text8xl", "56px", "64px"),
  ...createWeightVariants("text7xl", "48px", "56px"),
  ...createWeightVariants("text6xl", "40px", "48px"),
  ...createWeightVariants("text5xl", "36px", "44px"),
  ...createWeightVariants("text4xl", "32px", "48px"),
  ...createWeightVariants("text3xl", "28px", "36px"),
  ...createWeightVariants("text2xl", "24px", "32px"),
  ...createWeightVariants("text1xl", "20px", "28px"),
  ...createWeightVariants("textLg", "18px", "26px"),
  ...createWeightVariants("textBase", "16px", "24px"),
  ...createWeightVariants("textS", "14px", "20px"),
  ...createWeightVariants("textXs", "12px", "18px"),
  ...createDecorativeVariants("textLg", "18px", "26px"),
  ...createDecorativeVariants("textBase", "16px", "24px"),
  ...createDecorativeVariants("textS", "14px", "20px"),
  ...createDecorativeVariants("textXs", "12px", "18px"),
  dialogTitle: {
    fontWeight: 600,
    fontFamily: "Inter",
    fontSize: "14px",
    lineHeight: "20px",
  },
  dialogBody: {
    fontWeight: 500,
    fontFamily: "Inter",
    fontSize: "14px",
    lineHeight: "20px",
  },
  h7: {
    fontWeight: 700,
    fontSize: 16,
    textTransform: "capitalize",
    fontFamily: "Telegraf",
  },
};

export const sharedTypography: ThemeOptions["typography"] = {
  fontFamily: appFontFamily,
  h1: {
    fontWeight: 700,
  },
  h2: {
    fontWeight: 700,
  },
  h3: {
    fontWeight: 700,
  },
  h4: {
    fontWeight: 700,
  },
  h5: {
    fontWeight: 700,
  },
  h6: {
    fontWeight: 700,
  },
  subtitle1: {
    ...customTypographyVariants.textLg400
  },
  subtitle2: {
    fontWeight: 500,
    color: dsSurface.v400,
  },
  button: {
    fontFamily: "Optician-Sans",
  },
  ...customTypographyVariants,
};

export const typographyDefaultProps = {
  variant: "textBase500",
  color: "dsBase.v200",
} as const;
